import type { RefObject } from 'react';
import type { View } from 'react-native';
import html2canvas from 'html2canvas';

export interface ExportItem {
  ref: RefObject<View | null>;
  fileName: string;
  /** Tamanho final em pixels (1080 de largura). */
  width: number;
  height: number;
}

/**
 * No navegador a View vira um elemento do DOM: o html2canvas desenha na escala
 * certa para sair em 1080px nítido, em vez de esticar a prévia.
 */
async function toBlob(item: ExportItem): Promise<Blob> {
  const node = item.ref.current as unknown as HTMLElement | null;
  if (!node) throw new Error('post não montado');
  const canvas = await html2canvas(node, {
    scale: item.width / node.offsetWidth,
    useCORS: true,
    backgroundColor: null,
    logging: false,
  });
  return new Promise((resolve, reject) =>
    canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('falha ao gerar'))), 'image/png'),
  );
}

function download(blob: Blob, fileName: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** Baixa todas as imagens. */
export async function savePosts(items: ExportItem[]): Promise<boolean> {
  for (const item of items) {
    download(await toBlob(item), item.fileName);
  }
  return true;
}

/** Compartilhamento nativo do navegador (celular); sem suporte, baixa o arquivo. */
export async function sharePost(item: ExportItem): Promise<void> {
  const blob = await toBlob(item);
  const file = new File([blob], item.fileName, { type: 'image/png' });
  if (typeof navigator.canShare === 'function' && navigator.canShare({ files: [file] })) {
    try {
      await navigator.share({ files: [file] });
    } catch (err) {
      if ((err as Error).name !== 'AbortError') throw err;
    }
    return;
  }
  download(blob, item.fileName);
}

export const SAVE_LABEL = 'Baixar imagens';
